import {ChangeDetectorRef, Component, Inject, OnInit, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, Subject, Subscription} from 'rxjs';
import {LearnerProfileResponseDto} from '../../../../../micromate-learn-lib/src/lib/types/LearnerProfileResponseDto';
import {filter, map} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';
import {LearnAuthService} from '../../core/authentication/learn-auth.service';
import {AuthUntilUserRoleService} from '../../core/authentication/auth-until-user-role.service';
import {EventRecorderService} from '../../../../../micromate-learn-lib/src/lib/services/event/event-recorder.service';
import {ProfileRessource} from '../../../../../micromate-learn-lib/src/lib/services/rest/profile-ressource.service';
import {TabEventType} from '../../../../../micromate-learn-lib/src/lib/services/event/TabEventType';
import {NavigationService} from '../../core/navigation.service';
import {
    ProfilePseudonymService
} from '../../../../../micromate-learn-lib/src/lib/components/profile/profile-pseudonym.service';
import {PseudonymSettings} from '../../../../../micromate-learn-lib/src/lib/components/profile/PseudonymSettings';
import {LIB_AUTH_SERVICE_TOKEN} from '../../../../../micromate-learn-lib/src/lib/components/chatbot/libAuthService';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {
    UILocalizationService
} from '../../../../../micromate-learn-lib/src/lib/services/locale/u-i-localization.service';
import {ErrorNotificationService} from '../../core/error-notification.service';
import {
    UpdateLearnerProfileMainChannelRequestDto
} from '../../../../../micromate-learn-lib/src/lib/services/rest/api-types/UpdateLearnerProfileMainChannelRequestDto';
import {
    UpdateProfileLocaleRequestDto
} from '../../../../../micromate-learn-lib/src/lib/services/rest/api-types/UpdateProfileLocaleRequestDto';
import {
    UpdateLearningReminderRequestDto
} from '../../../../../micromate-learn-lib/src/lib/services/rest/api-types/UpdateLearningReminderRequestDto';

@Component({
    selector: 'mm-learning-profile',
    templateUrl: './learning-profile.component.html',
    styleUrls: ['./learning-profile.component.scss'],
    standalone: false
})
export class LearningProfileComponent implements OnInit, OnDestroy {
    public profileData: BehaviorSubject<LearnerProfileResponseDto | undefined> = new BehaviorSubject<LearnerProfileResponseDto | undefined>(undefined);
    public profileData$ = this.profileData.pipe(filter((value): value is LearnerProfileResponseDto => value !== undefined));

    public locale: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);
    public locale$ = this.locale.pipe(filter((value): value is string => value !== undefined));

    private profileUpdated: Subject<void> = new Subject<void>();
    public profileUpdated$: Observable<void> = this.profileUpdated.asObservable();

    public paramsSubscription = new Subscription();
    public openInConversation: boolean = false;
    public learnerId: string | undefined;

    public mainChannel$ = this.profileData$.pipe(filter((data): data is LearnerProfileResponseDto => data !== undefined),
        map(profileData => profileData.mainChannel));
    public allChannels$ = this.profileData$.pipe(filter((data): data is LearnerProfileResponseDto => data !== undefined),
        map(profileData => profileData.channels));

    constructor(private activatedRoute: ActivatedRoute,
                private profileService: ProfileRessource,
                @Inject(LIB_AUTH_SERVICE_TOKEN) public authService: LearnAuthService,
                public authUntilUserRoleService: AuthUntilUserRoleService,
                private router: Router,
                private eventRecorderService: EventRecorderService,
                private navigationService: NavigationService,
                private uiLocalizationService: UILocalizationService,
                private profilePseudonymService: ProfilePseudonymService,
                private toastr: ToastrService,
                private translate: TranslateService,
                public errorNotificationService: ErrorNotificationService,
                private changeDetectorRef: ChangeDetectorRef) {
    }

    public ngOnInit(): void {
        this.paramsSubscription = this.activatedRoute.queryParamMap.pipe(
            map(params => {
                this.learnerId = params.get('learnerid') ?? undefined;
                this.openInConversation = params.get('openInConversation') === 'true';
            }))
            .subscribe(async () => {
                this.changeDetectorRef.markForCheck();
                await this.getLearnerProfile();
                await this.recordTabEvents();
            });
    }

    public ngOnDestroy(): void {
        this.paramsSubscription.unsubscribe();
    }

    public close(): void {
        this.navigationService.closeProfile();
    }

    public savePseudonymSettings(pseudonymSettings: PseudonymSettings): void {
        this.profileService.savePseudonymSettingsForLearn(pseudonymSettings).subscribe(async () => {
            const profilePromise = this.profileService.getLearnerProfileForLearn();
            const profileData = await profilePromise;
            this.profileData.next(profileData);

        });
    }

    public async saveLocalData(data: UpdateProfileLocaleRequestDto): Promise<void> {
        const updatedProfile = await this.profileService.updateLocalForLearn(data);
        await this.uiLocalizationService.setLanguage(updatedProfile.locale.split('-')[0]);
        await this.authService.updateCurrentUser();
        this.locale.next(updatedProfile.locale);
        this.profileData.next(updatedProfile);
        this.profileUpdated.next();
    }

    public async saveMainChannelData(data: UpdateLearnerProfileMainChannelRequestDto): Promise<void> {
        const updatedProfile = await this.profileService.updateMainChannelForLearn(data);
        await this.authService.updateCurrentUser();
        this.profileData.next(updatedProfile);
        this.profileUpdated.next();
    }

    public async saveLearningReminderData(data: UpdateLearningReminderRequestDto): Promise<void> {
        const updatedProfile = await this.profileService.updateLearningReminderForLearn(data);
        await this.authService.updateCurrentUser();
        this.profileData.next(updatedProfile);
    }

    public generatePseudonym(): void {
        this.profilePseudonymService.pseudonymGenerationSubscription = this.profileService.generatePseudonymForLearn().subscribe(pseudonym =>
            this.profilePseudonymService.setNewPseudonym(pseudonym));
    }

    public changeMonthlyReport(monthlyReportState: boolean): void {
        this.profileService.saveMonthlyLearningReportStatusForLearn(monthlyReportState).subscribe(async () => {
            const profilePromise = this.profileService.getLearnerProfileForLearn();
            this.profileData.next(await profilePromise);
            this.toastr.success(this.translate.instant('profile.monthlyReportSaved') as string);
        });
    }

    private async getLearnerProfile(): Promise<void> {
        if (this.learnerId === undefined) {
            const profilePromise = this.profileService.getLearnerProfileForLearn();
            const profileData = await profilePromise;
            this.profileData.next(profileData);
            this.locale.next(profileData.locale);
        } else {
            const isAdmin = await this.authUntilUserRoleService.assertAdmin().toPromise();
            if (isAdmin !== true) {

                void this.router.navigate(['/learn']);
            } else {
                const profileData = await this.profileService.getLearnerProfileForLearn(this.learnerId);
                this.profileData.next(profileData);
                this.locale.next(profileData.locale);
            }
        }
    }

    private async recordTabEvents(): Promise<void> {
        await this.eventRecorderService.recordTabOpenedEventForLearn(this.learnerId, TabEventType.TabProfileOpened, this.eventRecorderService.createTabOpenEventPayloadForLearn(this.openInConversation));
    }


}
